import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ToastContext = createContext(null);

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'success', options = {}) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, action: options.action || null, persistent: !!options.persistent };
    // Cap visible toasts at 4 — drop the oldest when overflowing.
    // Without this, an SSE-driven page can stack a dozen banners and
    // collide with the sticky header on small phones.
    setToasts(prev => [...prev, toast].slice(-4));
    if (!toast.persistent) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    }
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Memoised so consumers that read the whole object (rather than
  // destructuring `showToast`) get a stable reference and don't trip
  // useCallback/useEffect dep-array churn.
  const value = useMemo(() => ({ showToast, dismiss }), [showToast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="fixed right-5 left-5 sm:left-auto z-50 flex flex-col gap-3 pointer-events-none"
        style={{ top: 'calc(1.25rem + env(safe-area-inset-top))' }}
      >
        {toasts.map(toast => (
          <div
            key={toast.id}
            role={toast.type === 'error' ? 'alert' : 'status'}
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
            className={`pointer-events-auto animate-slide-in-right flex items-center gap-3 px-4 py-3 rounded-lg shadow-2xl text-sm font-medium min-w-64 max-w-sm border backdrop-blur ${
              toast.type === 'error'   ? 'bg-red-500/15 text-red-200 border-red-500/30' :
              toast.type === 'warning' ? 'bg-amber-500/15 text-amber-200 border-amber-500/30' :
              toast.type === 'info'    ? 'bg-blue-500/15 text-blue-200 border-blue-500/30' :
                                         'bg-night-50 text-fg border-acid/40'
            }`}
          >
            <span className="flex-1">{toast.message}</span>
            {toast.action && (
              <button
                onClick={() => { toast.action.onClick?.(); dismiss(toast.id); }}
                className="text-acid font-semibold underline-offset-2 hover:underline shrink-0"
              >
                {toast.action.label}
              </button>
            )}
            <button
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss notification"
              className="opacity-60 hover:opacity-100 text-lg leading-none shrink-0"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
